import {
  AfterViewInit,
  Component,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { Store } from '@ngrx/store';
import { Subscription } from 'rxjs';
import { AbstractOnboardingPageComponent } from 'src/app/core/abstracts/components/onboarding-page.abstract';
import { CustomerTypes } from 'src/app/core/enums/customer-types.enum';
import { EventTypes } from 'src/app/core/enums/event.enum';
import { IIdValuePair } from 'src/app/core/interfaces/common/common/id-value-pair.interface';
import { IPageContent } from 'src/app/core/interfaces/common/content/page-content.interface';
import { IMultiOptions } from 'src/app/core/interfaces/onboarding/business-profile/business-multi-options.interface';
import { IBusinessProfile } from 'src/app/core/interfaces/onboarding/business-profile/business-profile.interface';
import { ITransition } from 'src/app/core/interfaces/onboarding/page-state/transition.interface';
import { PageState } from 'src/app/core/models/page-state/page-state.model';
import { BusinessProfileService } from 'src/app/core/services/onboarding/business-profile.service';
import { EnrollmentExtendedService } from 'src/app/core/services/onboarding/enrollment-extended.service';
import { PageStateService } from 'src/app/core/services/onboarding/page-state.service';
import { ServersideAlertService } from 'src/app/core/services/onboarding/serverside-alert.service';
import { IAppState } from 'src/app/core/store/common/interfaces/app-state.interface';
import { CustomerTypesAction } from 'src/app/core/store/onboarding/actions/business-profile.actions';
import { selectCustomerTypes } from 'src/app/core/store/onboarding/reducers+selectors/business-profile.selector';
import { destructSubscriptionArray } from 'src/app/core/ui-helpers/functions/destruct-subscription-array';
import { syncComponentHeightResize } from 'src/app/core/ui-helpers/functions/sync-component-height';
import { ToolTipComponent } from 'src/app/shared/common/tool-tip/tool-tip.component';
import { ShellComponent } from 'src/app/shared/onboarding/shell/shell.component';
import { ContentIDConstants } from './ContentIDConstants';

@Component({
  selector: 'app-customer-type',
  templateUrl: './customer-type.component.html',
  styleUrls: ['./customer-type.component.scss'],
})
export class CustomerTypeComponent
  extends AbstractOnboardingPageComponent
  implements OnInit, AfterViewInit, OnDestroy
{
  backState: PageState;
  nextState: PageState;
  skipState: PageState;
  homeState: PageState;
  customerTypeData: IMultiOptions[] = [];
  selectedCustomerTypeData: IIdValuePair[] = [];
  form: FormGroup;
  othersSelected = false;
  pageStateId: string;

  payload: IBusinessProfile;
  @ViewChild(ShellComponent) shellComponent: ShellComponent;
  @ViewChild(ToolTipComponent) questionTooltipComponent: ToolTipComponent;
  questionText: string;
  subscriptions: Subscription[] = [];

  constructor(
    private route: ActivatedRoute,
    private ps: PageStateService,
    private ees: EnrollmentExtendedService,
    private fb: FormBuilder,
    private bf: BusinessProfileService,
    private store: Store<IAppState>,
    private notificationService: ServersideAlertService
  ) {
    super();
  }

  async ngOnInit(): Promise<void> {
    this.subscriptions.push(
      this.route.data.subscribe(async (data) => {
        const { transitions } = data['data'];
        const { contentDict } = data['data'];
        this.populatePageStates(transitions);
        this.populatePageContent(contentDict);
        this.subscriptions.push(
          this.route.queryParams.subscribe((params) => {
            this.ees.updateBusinessProfileStateId(
              params['stateId'],
              this.homeState.id
            );
            this.pageStateId = params['stateId'];
          })
        );
        this.customerTypeData = data['options'];

        const props$ = this.store.select(selectCustomerTypes);
        this.subscriptions.push(
          props$.subscribe((props) => {
            if (props.customerTypes.length === 0) return;
            this.customerTypeData = props.customerTypes;
          })
        );

        const typeLen = this.customerTypeData.length;
        for (let i = 0; i < typeLen; i += 1) {
          if (
            this.customerTypeData[i].id === CustomerTypes.OTHER &&
            this.customerTypeData[i].selected
          )
            this.othersSelected = true;

          if (this.customerTypeData[i].id === CustomerTypes.OTHER) {
            this.form = this.fb.group({
              others: [
                this.customerTypeData[i].value !== this.customerTypeData[i].name
                  ? this.customerTypeData[i].value
                  : '',
              ],
            });
          }
        }
      })
    );
    this.payload = this.bf.getPayload();

    // Update payload with pagestate id of current page
    this.payload.PageStateId = this.pageStateId;
  }

  ngAfterViewInit(): void {
    this.subscriptions.push(
      syncComponentHeightResize(
        this.questionTooltipComponent,
        this.shellComponent.content,
        this.shellComponent.container
      )
    );
  }

  ngOnDestroy(): void {
    destructSubscriptionArray(this.subscriptions);
  }

  async submit() {
    let anySelected = false;
    const tempArr = this.customerTypeData.map((type: IMultiOptions) => {
      if (type.id === CustomerTypes.OTHER) {
        if (type.selected && this.form.get('others')?.value) anySelected = true;
        return {
          id: type.id,
          name: type.name,
          value: this.form.get('others')?.value,
          selected: type.selected,
        };
      }
      if (type.selected) anySelected = true;
      console.log("CUstomer type", type)
      console.log("CUstomer type submit", type.value)
      return {
        id: type.id,
        name: type.name,
        value: '',
        selected: type.selected,
      };
    });
    if (!anySelected) {
      this.notificationService.reportAlert(
        ['You must select one option.'],
        'warn'
      );
      return false;
    }

    this.store.dispatch(
      CustomerTypesAction({
        customerTypes: tempArr,
      })
    );
    this.payload.CustomerTypes = tempArr;
    const response = await this.bf.submitBusinessProfileData(this.payload);
    return response.isSuccessful;
  }

  populatePageStates(transitions: ITransition[]): void {
    this.backState = this.ps.getPageStateByEventAndTransitions(
      EventTypes.BACK,
      transitions
    );
    this.nextState = this.ps.getPageStateByEventAndTransitions(
      EventTypes.NEXT,
      transitions
    );
    this.skipState = this.ps.getPageStateByEventAndTransitions(
      EventTypes.SKIP,
      transitions
    );
    this.homeState = this.ps.getPageStateByEventAndTransitions(
      EventTypes.HOME,
      transitions
    );
  }

  populatePageContent(contentDict: Map<number, IPageContent>): void {
    this.questionText =
      contentDict.get(ContentIDConstants.Question)?.displayText ??
      'Who are your current customers? Select all that apply.';
  }

  async handleOutput(id: number) {
    // Handle the emitted output parameter value here
    const tempArr = this.customerTypeData.map((type: IMultiOptions) => {
      if (type.id === id) {
        if (type.id === CustomerTypes.OTHER && !type.selected)
          this.othersSelected = true;
        if (type.id === CustomerTypes.OTHER && type.selected)
          this.othersSelected = false;

        return {
          id: type.id,
          name: type.name,
          value: '',
          selected: !type.selected,
        };
      }
      return type;
    });
    this.customerTypeData = tempArr;

    this.store.dispatch(
      CustomerTypesAction({
        customerTypes: this.customerTypeData,
      })
    );
  }
}
